import { is_subassembly, subassembly_parent_design_uuid } from "@/utils/subassemblies";
import { backend_api_v2 } from '/src/utils/apiv2'
// import { today } from '/src/utils/time'
import bus from '/src/utils/event_bus'
import base from "./base"
import {
    COMPANY_PR_ALL,
    loadDesignFactory,
    loadActualAndItsDesignFactory,
} from "./selected_helpers.js"
import { defineStore } from 'pinia'
import { useCompanyStore } from "/src/stores/company"
import { useConstructiblesStore } from "/src/stores/constructibles"
import { useAppStore } from './app'
import { useSessionStore } from './session'
import { useFactoriesStore } from './factories'
import {
    countsIndicator, filesIndicator, runIsActive, runIsLive, runLocksMatrix,
} from '/src/utils/design_update.js'

// Last-call registry, same pattern as base.js / constructibles.js: the key
// holds the argument of the most recent call, and a response only commits if
// it still matches.
const LASTS = {}

// The attention poll (docs/update-designs-proposal.md §5.7): one timer for the
// whole app, running only while a design update is queued or running.
let attention_timer = null
const ATTENTION_POLL_MS = 10000
// A run that stopped answering may still finish; it is asked less often.
const ATTENTION_STALLED_POLL_MS = 60000

export const useSelectedStore = defineStore('selected', {
    state: () => ({
        // By date
        selected_date: "2000-01-01", // To be touched from component

        // UI MUTATED (as an uuid)
        pr_selection: "all",

        // ASYNC LOADED (as object)
        selected_project: COMPANY_PR_ALL,
        stdlib_mode: false,

        // Constructible selection
        selected_mod: null,
        selected_ass: null,
        selected_modd: null,
        selected_assd: null,

        // A unit the backend refused to a production user (a 404 with the
        // reason in its body): { uuid, level, code, production_line }. Fed by
        // the requests the viewer already makes; the banner shows it while the
        // route is on that unit.
        route_refusal: null,

        // Multipanel group selection (only used in mpgroup_viewer)
        // Shape: { group: {...}, members: [{ assembly_uuid, design_name, display_name, width_mm, gap_before_mm, files: [...] }, ...] }
        selected_multipanel_group: null,

        // Parts
        selected_parts: [],

        selected_hot_issues: [],


        // Left menu browser state
        show_delayed_constructibles: localStorage.getItem("show_delayed_constructibles") != "false",
        show_ready_constructibles: localStorage.getItem("show_ready_constructibles") != "false",
        max_shown_is_set: localStorage.getItem("max_shown_is_set") == "true",
        max_shown_constructibles: Number(localStorage.getItem("max_shown_constructibles") || 10),

        // Viewer list filters (by-project mode), driven by SelectorViewerList.
        // Deliberately NOT persisted, unlike the four flags above: a batch uuid
        // means nothing in another project, and a phase name that no longer
        // exists would silently empty the list at the next login.
        viewer_phase_filter: null,   // phase uuid
        viewer_batch_filter: null,   // batch uuid

        // What is waiting on the selected project, from `GET …/attention/`:
        // { files: { pending, count }, run, quantities }. Feeds the left menu
        // indicators, the Update designs button, the file chips and the
        // banner. null until loaded and outside a project.
        project_attention: null,
        // The matrix's own drift while Production counts is open: it counts
        // edits not saved yet, so the menu follows the button while typing.
        local_counts_drift: null,


    }),
    getters: {
        designRun(state) {
            return state.project_attention?.run || null
        },
        pendingFiles(state) {
            return state.project_attention?.files?.pending || {}
        },
        attentionQuantities(state) {
            return state.local_counts_drift || state.project_attention?.quantities || null
        },
        filesIndicator(state) {
            return filesIndicator(state.project_attention)
        },
        countsIndicator(state) {
            if (state.stdlib_mode) return { kind: "none" }
            return countsIndicator(state.project_attention, this.attentionQuantities)
        },
        // The quantities matrix is locked while designs are being updated.
        designUpdateLocksMatrix(state) {
            return runLocksMatrix(state.project_attention?.run)
        },
        projectIFCPS(state) {
            const company_store = useCompanyStore()
            return company_store.ifc_ps
                .find(ifcps => ifcps.uuid == state.selected_project?.ifc_parsing_setup)
        },
        areProjectResourcesBusy(state) {
            const pr_res = state.selected_project?.resources
            if (!pr_res) return false
            let to_return = false
            Object.keys(pr_res).forEach((section) => {
                pr_res[section].forEach(f => {
                    if (f.status?.includes("busy")) to_return = true
                })
            })
            return to_return
        },
        annotatedRouteActual(state) {
            const app_store = useAppStore()
            let rn = app_store.route_name
            if (state.selected_mod && ["mod_viewer"].includes(rn)) return { ...state.selected_mod, level: "module" }
            if (state.selected_ass && ["ass_viewer"].includes(rn)) return { ...state.selected_ass, level: "assembly" }
            if (state.selected_modd && ["modd_viewer"].includes(rn)) return { ...state.selected_modd, level: "module" }
            if (state.selected_assd && ["assd_viewer"].includes(rn)) return { ...state.selected_assd, level: "assembly" }
            if (state.selected_multipanel_group && rn === "mpgroup_viewer") {
                return { ...state.selected_multipanel_group.group, level: "multipanel_group" }
            }
            return null
        },
        annotatedRouteDesign(state) {
            const app_store = useAppStore()
            let rn = app_store.route_name
            if (["mod_viewer", "modd_viewer"].includes(rn)) return state.selected_modd
            if (["ass_viewer", "assd_viewer"].includes(rn)) return state.selected_assd
        },
        routeActualBatch() {
            const constructibles_store = useConstructiblesStore()
            let actual = this.annotatedRouteActual
            return constructibles_store.batches.find(b => b.uuid == actual?.batch)
        },

        selectedConstUuid: state => state.selected_ass?.uuid || state.selected_mod?.uuid,

        // The route's unit is on another line than the one the account has
        // selected. The backend only refuses such a unit to a production user
        // with a line; anyone else gets it, so the viewer has to see it on its
        // own. Same shape as `route_refusal`, so the banner reads both. No
        // line selected means every line, and then nothing is "another" line.
        routeOtherLine() {
            const actual = this.annotatedRouteActual
            if (!actual || !["assembly", "module"].includes(actual.level)) return null
            if (!actual.production_line) return null
            const session_store = useSessionStore()
            const line = session_store.selected_line_uuid
            if (!line || actual.production_line == line) return null
            const factories_store = useFactoriesStore()
            const pl = factories_store.production_lines.find(l => l.uuid == actual.production_line)
            return {
                uuid: actual.uuid,
                level: actual.level,
                code: "other_line",
                production_line: { uuid: actual.production_line, name: pl?.name || "" },
            }
        },


        // Boolean flags
        // The tipo, the rule the backend uses (BaseDesign.is_subassembly). The
        // analysis of the panel sets it together with the link to the panel.
        // Read off the route's design: `selected_assd` outlives the assembly
        // viewer, and a module opened after a subassembly took its IFC and BOM
        // from that subassembly's panel.
        isSubassd() { return is_subassembly(this.annotatedRouteDesign) },
        // The panel design the open subassembly is shown in (see the helper).
        subassdParentDesignUuid() {
            const design = this.annotatedRouteDesign
            if (!is_subassembly(design)) return null
            const constructibles_store = useConstructiblesStore()
            return subassembly_parent_design_uuid(
                design, this.annotatedRouteActual,
                constructibles_store.assemblies, constructibles_store.assembly_designs)
        }
    },
    actions: {
        // By Date
        setDate(date) { this.selected_date = date },

        // UID PR Selection
        setProjectSelection(selection) { this.pr_selection = selection },


        // Project selection
        setProjectMode() { this.stdlib_mode = false },
        setSTDLIBMode() { this.stdlib_mode = true },
        setCompanyWide() {
            this.pr_selection = "all"
            this.selected_project = COMPANY_PR_ALL
            this.clearViewerFilters()
        },
        setProject(data) {
            // Only on an actual change of project: putProject() and the meta
            // refreshers come back through here with the project the user is
            // already on, and dropping their filter then would be surprising.
            if (data?.uuid != this.selected_project?.uuid) this.clearViewerFilters()
            this.selected_project = data
            if (!("jobs" in this.selected_project.meta)) this.selected_project.meta.jobs = {}
        },
        refreshProject(data) {
            if (data.uuid != this.selected_project.uuid) return
            this.selected_project = data
            if (!("jobs" in this.selected_project.meta)) this.selected_project.meta.jobs = {}
        },
        clearProject() {
            this.selected_project = null
            this.clearViewerFilters()
        },



        // Update designs and what is waiting (docs/update-designs-proposal.md)
        projectUrl(project_uuid = this.selected_project?.uuid) {
            const key = this.stdlib_mode ? "standard_libraries" : "projects"
            return `company/${key}/${project_uuid}/`
        },
        clearAttention() {
            clearTimeout(attention_timer)
            attention_timer = null
            this.project_attention = null
            this.local_counts_drift = null
        },
        setLocalCountsDrift(drift) {
            this.local_counts_drift = drift
        },
        setAttentionRun(run) {
            if (!this.project_attention) this.project_attention = { files: { pending: {}, count: 0 }, quantities: null, run }
            else this.project_attention = { ...this.project_attention, run }
            this.scheduleAttention()
        },
        scheduleAttention() {
            clearTimeout(attention_timer)
            attention_timer = null
            const run = this.project_attention?.run
            if (!runIsActive(run)) return
            attention_timer = setTimeout(() => this.loadAttention(),
                run.stalled ? ATTENTION_STALLED_POLL_MS : ATTENTION_POLL_MS)
        },
        loadAttention() {
            const session_store = useSessionStore()
            const project_uuid = this.selected_project?.uuid
            if (!project_uuid || project_uuid == "all" || !session_store.iam_manager_or_admin) {
                this.clearAttention()
                return Promise.resolve(null)
            }
            LASTS["attention"] = project_uuid
            return backend_api_v2.get(this.projectUrl(project_uuid) + "attention/")
                .then(({ data }) => {
                    if (LASTS["attention"] != project_uuid || this.selected_project?.uuid != project_uuid) return null
                    const before = this.project_attention?.run
                    this.project_attention = data
                    const after = data?.run
                    // A run that was going and is not any more: the page reloads
                    // what it changed (store_watchers/selected.js).
                    if (runIsActive(before) && !runIsActive(after))
                        bus.emit("events/designUpdate/finished", after)
                    this.scheduleAttention()
                    return data
                })
                .catch(e => {
                    console.log(`Cant load project attention: ${e}`)
                    this.scheduleAttention()
                    return null
                })
        },
        previewDesignUpdate(files) {
            return backend_api_v2.post(this.projectUrl() + "update_designs/preview/", { files: files || null })
                .then(({ data }) => data)
        },
        launchDesignUpdate(files, options) {
            return backend_api_v2.post(this.projectUrl() + "update_designs/", { files: files || null, options })
                .then(({ data }) => {
                    this.setAttentionRun(data)
                    bus.emit("events/cloudstorage/reloadProjectPackages")
                    return data
                })
        },
        stopDesignUpdate() {
            return backend_api_v2.post(this.projectUrl() + "update_designs/stop/")
                .then(({ data }) => {
                    this.setAttentionRun(data)
                    bus.emit("events/designUpdate/finished", data)
                    return data
                })
        },
        dismissDesignUpdate() {
            return backend_api_v2.post(this.projectUrl() + "update_designs/dismiss/")
                .then(({ data }) => {
                    this.setAttentionRun(data)
                    return data
                })
        },

        // Constructibles
        setAssembly: base.actions.setter("selected_ass"),
        setModule: base.actions.setter("selected_mod"),
        setAssemblyDesign: base.actions.setter("selected_assd"),
        setModuleDesign: base.actions.setter("selected_modd"),

        // Refusals. Only a production user's 404 is one: a manager is never
        // line-filtered, so theirs means the unit does not exist.
        noteRefusal(uuid, level, error) {
            const session_store = useSessionStore()
            if (!uuid || error?.response?.status != 404 || !session_store.iam_prod_worker) return
            const data = error.response.data || {}
            this.route_refusal = {
                uuid,
                level,
                code: data.code || null,
                production_line: data.production_line || null,
            }
        },
        clearRefusal(uuid) {
            if (this.route_refusal?.uuid == uuid) this.route_refusal = null
        },

        // Parts
        setSelectedParts(parts) { this.selected_parts = parts },

        // ISSUES
        setHotIssues(issues) { this.selected_hot_issues = issues },





        // Other Project methods
        loadProject(project_uuid) {
            let url
            if (this.stdlib_mode) url = 'company/standard_libraries/'
            else url = 'company/projects/'

            // Register last call (ensure last called fills). Switching project
            // twice leaves two requests in flight; without this the slower one
            // wins and the store ends up on a project the user already left.
            LASTS["selected_project"] = project_uuid

            return backend_api_v2.get(url + project_uuid + '/')
                .then(({ data }) => {
                    // Reject prebounce
                    if (LASTS["selected_project"] != project_uuid) {
                        console.log("[i] Rejecting project commit. Not last call.")
                        return data
                    }
                    this.setProject(data)
                    this.setProjectSelection(data.uuid)
                    return data
                })
                .catch(e => {
                    console.log(`Cant load project: ${e}`)
                })
        },

        putProject(payload) {
            const company_store = useCompanyStore()
            // The putter has already told the user when the save failed (403,
            // 404) and resolves nothing then: keep the project on screen.
            return company_store.putProject(payload)
                .then((res) => {
                    if (!res?.data) return undefined
                    this.setProject(res.data)
                    return res.data
                })
        },

        // PHASES
        // Add, rename, reorder and delete each go in one request, applied in one
        // transaction on the backend, which answers with the whole project. A
        // phase is named by its uuid (or its name). On failure the user is told,
        // with the backend's reason when it gives one, and nothing is returned.
        phaseRequest(action, payload, project_uuid = this.selected_project?.uuid) {
            const company_store = useCompanyStore()
            return backend_api_v2.post(`company/projects/${project_uuid}/phases/${action}/`, payload)
                .then(({ data }) => {
                    company_store.updateProject(data)
                    if (data.uuid == this.selected_project?.uuid) this.setProject(data)
                    return data
                })
                .catch(e => {
                    const detail = e.response?.data?.detail || JSON.stringify(e.response?.data || e.message)
                    bus.emit('notification', { timeout: 6000, text: detail, color: "error" })
                    return undefined
                })
        },
        addPhase({ name, position }) {
            return this.phaseRequest("add", { name, position })
        },
        // `phase` is the phase uuid.
        async renamePhase({ phase, name }) {
            const project = await this.phaseRequest("rename", { phase, name })
            // The backend renamed every batch of the phase in the same transaction;
            // the loaded ones show the old name until they are reloaded.
            if (project) useConstructiblesStore().renamePhaseInBatches(phase, name)
            return project
        },
        reorderPhases(phases) {
            return this.phaseRequest("reorder", { phases })
        },
        deletePhase(phase) {
            return this.phaseRequest("delete", { phase })
        },


        // CONSTRUCTIBLES
        loadAssembly: loadActualAndItsDesignFactory("Assembly", "AssemblyDesign", "constructibles/assemblies"),
        loadModule: loadActualAndItsDesignFactory("Module", "ModuleDesign", "constructibles/modules"),
        loadAssemblyDesign: loadDesignFactory("AssemblyDesign", "constructibles/assembly_designs"),
        loadModuleDesign: loadDesignFactory("ModuleDesign", "constructibles/module_designs"),

        setMultipanelGroup(payload) { this.selected_multipanel_group = payload },
        clearMultipanelGroup() { this.selected_multipanel_group = null },
        loadMultipanelGroup(uuid) {
            const app_store = useAppStore()
            app_store.pushLoading("MultipanelGroup")
            return backend_api_v2.get(`constructibles/multipanel_groups/${uuid}/viewer_data/`)
                .then(async ({ data }) => {
                    // Fetch each member's viewer_bom JSON in parallel so the
                    // multipanel viewer can apply per-zone visibility filtering
                    // (mirroring the assembly viewer's BOM-based hiding).
                    const members = data.members || []
                    await Promise.all(members.map(async m => {
                        if (!m.viewer_bom_url) return
                        try {
                            const r = await fetch(m.viewer_bom_url)
                            if (r.ok) m.bom = await r.json()
                            else m.bom = []
                        } catch (e) {
                            console.log(`Cant fetch viewer_bom for member ${m.assembly_uuid}: ${e}`)
                            m.bom = []
                        }
                    }))
                    this.setMultipanelGroup(data)
                    return data
                })
                .catch(e => {
                    console.log(`Cant load multipanel group viewer data: ${e}`)
                    this.clearMultipanelGroup()
                })
                .finally(() => {
                    app_store.popLoading("MultipanelGroup")
                })
        },


        // PARTS
        loadPart(uuid) {
            const app_store = useAppStore()
            app_store.pushLoading("viewer")
            backend_api_v2.get(`/constructibles/parts/${uuid}/?serialize_design=true`)
                .then(({ data }) => {
                    this.setSelectedParts([data])
                })
                .catch(e => {
                    console.log(`Cant load part: ${e}`)
                })
                .finally(() => {
                    app_store.popLoading("viewer")
                })
        },
        loadSelectedParts(gids) {
            let level = "assembly"
            let cuuid = this.selected_ass?.uuid
            if (!cuuid) {
                level = "module"
                cuuid = this.selected_mod?.uuid
            }
            if (!cuuid) {
                return  // This is mostly for designs mode
            }
            let gid__in = gids.join(",")
            console.log("LOADING SELECTION", cuuid, gid__in)
            return backend_api_v2.get(`constructibles/parts/?gid__in=${gid__in}&${level}=${cuuid}&serialize_design=true`)
                .then(({ data }) => {
                    this.setSelectedParts(data.results)
                    return data
                })
                .catch(e => {
                    console.log(`Cant load parts: ${e}`)
                })
        },

        // ISSUES
        loadHotIssues: base.actions.loader_query("records/issues/hot", "HotIssues"),



        // Left menu browser state    
        toggleShowDelayedConstructibles() {
            this.show_delayed_constructibles = !this.show_delayed_constructibles
            localStorage.setItem("show_delayed_constructibles", this.show_delayed_constructibles)
        },
        toggleShowReadyConstructibles() {
            this.show_ready_constructibles = !this.show_ready_constructibles
            localStorage.setItem("show_ready_constructibles", this.show_ready_constructibles)
        },
        toggleMaxShownIsSet() {
            this.max_shown_is_set = !this.max_shown_is_set
            localStorage.setItem("max_shown_is_set", this.max_shown_is_set)
        },
        setMaxShownConstructibles(max) {
            this.max_shown_constructibles = max
            localStorage.setItem("max_shown_constructibles", max)
        },

        // Viewer list filters
        setViewerPhaseFilter(phase) {
            this.viewer_phase_filter = phase || null
            this.viewer_batch_filter = null
        },
        setViewerBatchFilter(batch) {
            // Takes the batch object, not its uuid: the phase comes with it, so
            // the button can label both of its lines without a second lookup,
            // and stepping across a phase boundary relabels itself.
            this.viewer_batch_filter = batch?.uuid || null
            this.viewer_phase_filter = batch?.phase_uuid || null
        },
        clearViewerFilters() {
            this.viewer_phase_filter = null
            this.viewer_batch_filter = null
        }
    },
})
